import {
  useEffect,
  useRef,
  type ComponentProps,
  type ElementType,
} from 'react';
import { Combobox } from '@headlessui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { type ServingSize } from '@hungryroot/rest';
import { type InventoriedAlgoliaItem } from '../../InventoriedAlgoliaItem';
import { SearchResult } from '../SearchResult/SearchResult';
import styles from './SearchResultsList.module.css';

interface Props extends Partial<Pick<ComponentProps<ElementType>, 'onScroll'>> {
  hasMore: boolean;
  onLoadMore: () => void;
  className?: string;
  results?: Array<{
    item: SearchResult;
    status: InventoriedAlgoliaItem['status'];
    dietaryRestrictions?: InventoriedAlgoliaItem['dietaryRestrictions'];
  }>;
  customerServingSize?: ServingSize;
  isFetchingNextPage: boolean;
}

export function SearchResultsList({
  onScroll,
  className,
  isFetchingNextPage,
  customerServingSize,
  results,
  onLoadMore,
  hasMore,
}: Props) {
  const resultsRef = useRef<HTMLDivElement>(null);
  const resultsLength = results === undefined ? 0 : results.length;
  const rowVirtualizer = useVirtualizer({
    // we're going to pop the "loading ui" onto the end
    // of the virtual list
    count: isFetchingNextPage === true ? resultsLength + 1 : resultsLength,
    getScrollElement: () => resultsRef.current,
    estimateSize: () => 90,
    overscan: 5,
  });
  const items = rowVirtualizer.getVirtualItems();
  const { length: itemsLength } = items;
  const lastItem = items[itemsLength - 1];
  const lastItemIndex = lastItem === undefined ? null : lastItem.index;

  useEffect(() => {
    if (lastItemIndex === null) {
      return;
    }

    if (
      lastItemIndex === resultsLength - 1 &&
      hasMore === true &&
      isFetchingNextPage === false
    ) {
      onLoadMore();
    }
  }, [
    hasMore,
    onLoadMore,
    resultsLength,
    isFetchingNextPage,
    itemsLength,
    lastItemIndex,
  ]);

  if (results === undefined) {
    // loading
    return (
      <>
        <SearchResult isLoading={true} />
        <SearchResult isLoading={true} />
        <SearchResult isLoading={true} />
        <SearchResult isLoading={true} />
        <SearchResult isLoading={true} />
      </>
    );
  }

  return (
    <div
      className={`${styles.results} ${className}`}
      onScroll={onScroll}
      ref={resultsRef}
      data-testid="container"
      key="results"
    >
      {/* headlessui added a feature to automattically select the first option
    by default when the input is focused with no opt-out functionality
    https://github.com/tailwindlabs/headlessui/pull/1279 the experience we
    want when a using is searching is that no option is selected by
    default and what the user searched in their input is respected,
    therefore, we need to develop create a "fake" and hidden first option,
    and custom logic to handle this user experience */}
      <Combobox.Option value=""></Combobox.Option>
      {items.map((virtualColumn) => {
        const { index } = virtualColumn;

        if (isFetchingNextPage === true && index === lastItemIndex) {
          // Render the loading UI
          // at the bottom of the list
          return (
            <div
              key="loading"
              style={{
                transform: `translateY(${virtualColumn.start}px)`,
              }}
              data-index={index}
              ref={rowVirtualizer.measureElement}
            >
              <SearchResult isLoading={true} />
            </div>
          );
        }

        const result = results[index];
        return (
          <Combobox.Option
            key={result.item.objectID}
            ref={rowVirtualizer.measureElement}
            data-index={index}
            style={{
              transform: `translateY(${virtualColumn.start}px)`,
            }}
            value={result}
            className={styles.option}
          >
            {({ active }) => (
              <>
                <SearchResult
                  result={result.item}
                  status={result.status}
                  dietaryRestrictions={result.dietaryRestrictions}
                  active={active}
                  isLoading={false}
                  customerPlanServingSize={customerServingSize}
                />
                <hr></hr>
              </>
            )}
          </Combobox.Option>
        );
      })}
    </div>
  );
}
